/**
 * LIVEINFRA wire shapes — now RE-EXPORTED from the canonical contract mirror
 * (`web/src/api/types.ts`, frozen with models.py per DECISIONS #46/#47/#48).
 * The Slice-1 temporary local mirror is gone; the aliases below keep the
 * feature's original names (`LiveOverlay`, `LiveEnvOverlay`, …) so consumers
 * read naturally while the contract stays single-sourced.
 *
 * Client-only vocabulary (view environments, disclosure fallbacks) stays
 * here — it is presentation, not contract.
 */

import type { LiveSourceDisclosure, ProvenanceResponse } from '../../api/types';

export type {
  ConnectInfraSourceRequest,
  InfraSourceKind,
  InfraSourceState,
  LiveDeploy,
  LiveEnvironmentOverlay,
  LiveInfraOverlay,
  LiveQueueState,
  LiveRedisState,
  LiveServiceState,
  LiveSourceDisclosure,
  LiveSourceState,
  LiveSourceStatus,
  LiveStoreState,
  UnmatchedService,
} from '../../api/types';

// Feature-local aliases (the Slice-1 names, kept so call sites read well).
export type { LiveInfraOverlay as LiveOverlay } from '../../api/types';
export type { LiveEnvironmentOverlay as LiveEnvOverlay } from '../../api/types';
export type { InfraSourceKind as LiveSourceKind } from '../../api/types';
export type { ConnectInfraSourceRequest as ConnectSourceRequest } from '../../api/types';
export type { InfraSourceState as InfraSource } from '../../api/types';

/**
 * The fallback view environments. The real list comes from the product's own
 * services now (2026-09-07; owner: "environments should be based on the
 * codebase, not hard coded to prod staging dev") and rides the overlay as
 * `environment_list`; these three stand in only until it loads or when the
 * code names none. Every environment is always read in one poll.
 */
export const VIEW_ENVIRONMENTS = ['prod', 'staging', 'dev'] as const;
/** An environment is just its name now, not a fixed union. */
export type ViewEnvironment = string;

/**
 * The environment to VIEW, held to the ones that exist (review 2026-09-07): the
 * remembered choice (or the `prod` default) may name an environment this product
 * does not have — a staging-only product, a renamed one — and then nothing on
 * the switcher would be pressed and the numbers would read as empty. The first
 * real environment stands in.
 */
export function clampViewEnv(env: string, known: readonly string[]): string {
  return known.length === 0 || known.includes(env) ? env : known[0]!;
}

/** The environments to show, from the overlay when it is loaded. */
export function viewEnvironmentsOf(
  overlay: { environment_list?: readonly string[] } | null | undefined,
): readonly string[] {
  const list = overlay?.environment_list;
  return list !== undefined && list.length > 0 ? list : VIEW_ENVIRONMENTS;
}

// ---------------------------------------------------------------------------
// The §4.1 disclosure block (served static from models.py via the
// provenance payload — the UI hardcodes nothing it can receive)
// ---------------------------------------------------------------------------

/** Kept as the render card's historical name; shape = the canonical card. */
export type RenderCliDisclosure = LiveSourceDisclosure;

export interface LiveSourcesDisclosure {
  render_cli?: LiveSourceDisclosure;
  redis?: LiveSourceDisclosure;
  [key: string]: unknown;
}

/**
 * Fallback copy for the connect sheet while the server's `live_sources`
 * block is not yet on the provenance payload — from LIVEINFRA.md §4.1
 * (which is itself the source for models.py). The payload, when present,
 * always wins; these constants exist so the consent flow never shows an
 * empty promise.
 */
export const RENDER_CLI_DISCLOSURE_FALLBACK: LiveSourceDisclosure = {
  runs: [
    'GET /services',
    'GET /services/<service-id>/deploys',
    'GET /postgres',
    'GET /key-value',
    'GET /environments/<environment-id>',
  ],
  scope: "reads metadata for every service and data store in your Render workspace, not just this repo's",
  never: [
    'deploy',
    'restart',
    'scale',
    'ssh',
    'psql',
    'jobs',
    'any request other than the reads listed above',
    'a POST, PATCH or DELETE of any kind',
  ],
  credentials:
    'your Render API key, stored like your Anthropic key, sent as one Authorization header to Render and nowhere else, and deleted when the last codebase using it disconnects',
  cadence:
    'every 60s while a loremap view is open; stops within 5 minutes of the last look and when loremap closes',
};

/** §4.1 redis card fallback (Slice 2 — the five-command wrapper surface). */
export const REDIS_DISCLOSURE_FALLBACK: LiveSourceDisclosure = {
  runs: [
    'PING',
    'LLEN <queue>',
    'HLEN unacked (broker-wide)',
    'ZCARD unacked_index',
    'TYPE <queue>',
  ],
  scope: null,
  never: [
    'writes of any kind',
    'KEYS/SCAN',
    'reading task payloads (LRANGE/GET/HVALS)',
    "celery control messages (they publish to your broker: that's a write)",
  ],
  credentials:
    'connection string stored like your Anthropic key (keyring/0600, redacted from all logs), delete anytime. A default-user string could write; loremap never will. Create the recommended ACL user to remove even the possibility.',
  cadence:
    'every 15s idle, 2s in live mode, while a loremap view is open; stops within 5 minutes of the last look',
};

/**
 * Read the (contract-amended) `live_sources` block off a provenance
 * payload. Optional-typed access, never a hard cast of the whole payload:
 * pre-amendment servers simply lack the field.
 */
export function liveSourcesOf(
  provenance: ProvenanceResponse | undefined,
): LiveSourcesDisclosure | null {
  if (provenance === undefined) return null;
  const block = (provenance as { live_sources?: unknown }).live_sources;
  if (typeof block !== 'object' || block === null || Array.isArray(block)) return null;
  return block as LiveSourcesDisclosure;
}

function disclosureOrFallback(
  block: unknown,
  fallback: LiveSourceDisclosure,
): LiveSourceDisclosure {
  if (
    typeof block === 'object' &&
    block !== null &&
    Array.isArray((block as LiveSourceDisclosure).runs)
  ) {
    return block as LiveSourceDisclosure;
  }
  return fallback;
}

/** The render_cli disclosure: payload if served, else the §4.1 fallback. */
export function renderCliDisclosure(
  provenance: ProvenanceResponse | undefined,
): LiveSourceDisclosure {
  return disclosureOrFallback(liveSourcesOf(provenance)?.render_cli, RENDER_CLI_DISCLOSURE_FALLBACK);
}

/** The redis disclosure: payload if served, else the §4.1 fallback. */
export function redisDisclosure(
  provenance: ProvenanceResponse | undefined,
): LiveSourceDisclosure {
  return disclosureOrFallback(liveSourcesOf(provenance)?.redis, REDIS_DISCLOSURE_FALLBACK);
}
